import { OnlyViewHoc } from 'hocs'
import * as React from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'

import { getFio } from 'helpers/formatText'
import {
  useGetKartomatsQuery,
  useLazyGetSupervisorsQuery,
} from 'redux/services/common'
import { logisticApi } from 'redux/services/logistic'

import { AppButton } from 'components/AppButton'
import AppCkeckbox from 'components/AppCkeckbox'
import { AppModal } from 'components/AppModal'
import { AppSelect } from 'components/AppSelect'
import { InputSelect } from 'components/InputSelect'
import { TextField } from 'components/TextField'
import { KG_PHONE_MAX_LENGTH, TextFieldPhone } from 'components/TextFieldPhone'

import { divisionLabels } from 'constants/labels'
import { DivisionType } from 'models/common'
import { Region } from 'models/region'
import { Role } from 'models/role'
import { ISubvision } from 'models/subvision'
import {
  UnitBranch,
  User,
  UserDTO,
  UserPermission,
  UserRole,
} from 'models/user'

import { ReactComponent as AddIcon } from 'assets/images/svg/add.svg'

import styles from './UsersFormModal.module.scss'
import { mbonusDepatments } from './mbonusDepatments'
import { permissions } from './permissions'

const AVATAR_ICON = require('assets/images/png/avatar.png')

const MAX_SHOW_KARTOMAT = 2

interface Props {
  onClose(): void
  disabled: boolean
  onSave(user: UserDTO, file: File | null): Promise<unknown>
  user?: User | null
  title: string
  button: string
  roles: Role[]
  regions: Region[]
  subvisions: ISubvision[]
  mainBranchs: UnitBranch[]
  unitsBranchs: UnitBranch[]
}
export const UsersFormModal: React.FC<Props> = ({
  onSave,
  onClose,
  disabled,
  user,
  title,
  button,
  roles,
  regions,
  subvisions,
  mainBranchs,
  unitsBranchs,
}) => {
  const [file, setFile] = React.useState<File | null>(null)
  const [preview, setPreview] = React.useState<string>('')
  const [fetchSupers, supersState] = useLazyGetSupervisorsQuery()
  const projects = logisticApi.useGetProjectsQuery()
  const { data: kartomats } = useGetKartomatsQuery({ free_only: true })
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    resetField,
    watch,
    setError,
  } = useForm<UserDTO>({
    defaultValues: user
      ? {
          ...user,
          region: user.region?.id,
          units_lending: user.units_lending?.id,
          mbonus_department: user?.mbonus_department || '',
          main_branch: user.main_branch?.id,
          branches: user.branches?.id,
          kartomat: user.kartomat?.map(item => item.id),
        }
      : {
          user_permissions: [],
        },
  })
  const watchedRole = useWatch({ control, name: 'role' })
  const watchedRegion = Number(useWatch({ control, name: 'region' }))
  const can_credit = useWatch({
    control,
    compute: user => user.user_permissions.includes('can_credit'),
  })
  const can_credit_2 = useWatch({
    control,
    compute: user => user.user_permissions.includes('can_credit_2'),
  })
  const can_installment = useWatch({
    control,
    compute: user => user.user_permissions.includes('can_installment'),
  })
  const can_kartomat = useWatch({
    control,
    compute: user => user.user_permissions.includes('can_kartomat'),
  })
  const can_m_bonus = useWatch({
    control,
    compute: user => user.user_permissions.includes('can_m_bonus'),
  })
  const can_murabaha = useWatch({
    control,
    compute: user => user.user_permissions.includes('can_murabaha'),
  })
  const can_uni_murabaha = useWatch({
    control,
    compute: user => user.user_permissions.includes('can_uni_murabaha'),
  })
  const can_subscribe_services = useWatch({
    control,
    compute: user => user.user_permissions.includes('can_subscribe_services'),
  })
  const subvisionAvailable =
    can_credit ||
    can_credit_2 ||
    can_installment ||
    can_murabaha ||
    can_uni_murabaha ||
    can_subscribe_services
  const canUseKartomat = can_kartomat && watchedRole === UserRole.Supervisor
  const watchedAvatar = useWatch({
    control,
    name: 'avatar',
  })
  const watchedPermissions = useWatch({ control, name: 'user_permissions' })

  const togglePermission = (permission: UserPermission) => {
    const permissionIndex = watchedPermissions.indexOf(permission)

    if (permissionIndex === -1) {
      setValue('user_permissions', [...watchedPermissions, permission])
    } else {
      setValue(
        'user_permissions',
        watchedPermissions
          .slice(0, permissionIndex)
          .concat(watchedPermissions.slice(permissionIndex + 1)),
      )
    }
  }

  const handleChangeDeliveryPermissions = (
    updatePermission: UserPermission,
    removePermission: UserPermission,
  ) => {
    const updatePermissionIndex = watchedPermissions.indexOf(updatePermission)
    const filteredPermissions = watchedPermissions.filter(
      permission => permission !== removePermission,
    )

    if (updatePermissionIndex === -1) {
      setValue('user_permissions', [...filteredPermissions, updatePermission])
    } else {
      setValue(
        'user_permissions',
        filteredPermissions
          .slice(0, updatePermissionIndex)
          .concat(filteredPermissions.slice(updatePermissionIndex + 1)),
      )
    }
  }

  React.useEffect(() => {
    if (!file) {
      return
    }

    const objectUrl = URL.createObjectURL(file)
    setPreview(objectUrl)

    // free memory when ever this component is unmounted
    return () => URL.revokeObjectURL(objectUrl)
  }, [file])

  const isSupervisorsAvailable =
    [UserRole.Consultant, UserRole.SPECIALIST_B2B].includes(watchedRole) &&
    watchedRegion

  React.useEffect(() => {
    if (isSupervisorsAvailable) {
      fetchSupers({ region: watchedRegion })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSupervisorsAvailable, watchedRole, watchedRegion])

  const branchs = watch('type_division')
    ? watch('type_division') === DivisionType.M
      ? mainBranchs
      : unitsBranchs
    : []
  const branchKeyField =
    watch('type_division') === DivisionType.M ? 'main_branch' : 'branches'

  const onSubmit = async (values: UserDTO) => {
    const res = await onSave(values, file)

    try {
      const errors = (res as any).errors as Record<string, string[]>

      Object.entries(errors).forEach(([key, messages]) =>
        setError(key as keyof UserDTO, {
          type: 'custom',
          message: Array.isArray(messages) ? messages[0] : messages,
        }),
      )
    } catch (error) {
      console.error(error)
    }
  }

  const kartomatOptions = [...(user?.kartomat || []), ...(kartomats || [])].map(
    item => ({
      value: item.id,
      title: `${item.name} (${item.address})`,
    }),
  )

  return (
    <AppModal onClose={onClose} title={title}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.avatarContainer}>
          <div className={styles.avatarBg} />
          <label htmlFor="image">
            <input
              accept="image/*"
              onChange={e => {
                setFile(e.currentTarget.files?.[0] || null)
              }}
              type="file"
              id="image"
            />
            <div className={styles.avatarImageContainer}>
              <div className={styles.avatarImage}>
                <img src={watchedAvatar || preview || AVATAR_ICON} alt="" />
              </div>
              <div className={styles.button}>
                <AddIcon />
                <span>Добавить</span>
              </div>
            </div>
          </label>
        </div>
        <div className={styles.content}>
          <div className={styles.row}>
            <div className={styles.rowItem}>
              <Controller
                control={control}
                name="role"
                rules={{
                  required: 'Выберите роль',
                }}
                render={({ field: { value } }) => {
                  return (
                    <AppSelect
                      required
                      size="extra_small"
                      label="Роль"
                      withError
                      error={errors.role?.message}
                      text={
                        value ? roles.find(r => r.code === value)?.name : ''
                      }
                      onChangeValues={values => {
                        const role = values?.[0]
                        if (role) {
                          setValue('role', role)
                          resetField('project')
                          resetField('region')
                          setError('role', {
                            message: '',
                          })
                        }
                      }}
                      list={roles.map(item => ({
                        value: item.code,
                        title: item.name,
                      }))}
                    />
                  )
                }}
              />
            </div>
            {UserRole.Executor === watch('role') && (
              <div className={styles.rowItem}>
                <Controller
                  control={control}
                  name="project"
                  key="project"
                  rules={{
                    required: 'Выберите проект',
                  }}
                  render={({ field: { value } }) => {
                    return (
                      <AppSelect
                        required
                        size="extra_small"
                        label="Проект"
                        withError
                        error={errors.project?.message}
                        text={
                          value
                            ? projects.data?.find(item => item.id === value)
                                ?.name
                            : ''
                        }
                        onChangeValues={values => {
                          if (values?.[0]) {
                            setValue('project', values?.[0])
                            setError('project', {
                              message: '',
                            })
                          }
                        }}
                        list={
                          projects.data?.map(item => ({
                            value: item.id,
                            title: item.name,
                          })) || []
                        }
                      />
                    )
                  }}
                />
              </div>
            )}

            {![
              UserRole.Author,
              UserRole.Executor,
              UserRole.Deliveryman,
              UserRole.ControllerUKC,
              UserRole.Specialist,
            ].includes(watchedRole) ? (
              <div className={styles.rowItem}>
                <Controller
                  control={control}
                  name="region"
                  key="region"
                  rules={{
                    required: 'Выберите регион',
                  }}
                  render={({ field: { value } }) => {
                    return (
                      <AppSelect
                        required
                        size="extra_small"
                        label="Регион"
                        withError
                        error={errors.region?.message}
                        text={
                          value
                            ? regions.find(item => item.id === value)?.name
                            : ''
                        }
                        onChangeValues={values => {
                          if (values?.[0]) {
                            setValue('region', values?.[0])
                            setError('region', {
                              message: '',
                            })
                          }
                        }}
                        list={regions.map(item => ({
                          value: item.id,
                          title: item.name,
                        }))}
                      />
                    )
                  }}
                />
              </div>
            ) : null}
          </div>

          {[UserRole.Deliveryman].includes(watchedRole) && (
            <>
              <h3>Может работать с:</h3>

              <div className={styles.row}>
                <div className={styles.rowItem}>
                  <Controller
                    control={control}
                    name="user_permissions"
                    render={({ field: { value } }) => {
                      return (
                        <AppCkeckbox
                          label="картами?"
                          checked={value.includes('delivery_can_cart')}
                          onChange={() =>
                            handleChangeDeliveryPermissions(
                              'delivery_can_cart',
                              'delivery_can_mpost',
                            )
                          }
                        />
                      )
                    }}
                  />
                </div>
                <div className={styles.rowItem}>
                  <Controller
                    control={control}
                    name="user_permissions"
                    render={({ field: { value } }) => {
                      return (
                        <AppCkeckbox
                          label="смартпос?"
                          checked={value.includes('delivery_can_mpost')}
                          onChange={() =>
                            handleChangeDeliveryPermissions(
                              'delivery_can_mpost',
                              'delivery_can_cart',
                            )
                          }
                        />
                      )
                    }}
                  />
                </div>
              </div>
              <br />
            </>
          )}

          <div className={styles.row}>
            <div className={styles.rowItem}>
              <Controller
                control={control}
                name="type_division"
                render={({ field: { value } }) => {
                  return (
                    <AppSelect
                      size="extra_small"
                      label="Тип подразделения"
                      withError
                      error={errors.type_division?.message}
                      text={value ? divisionLabels[value] : ''}
                      onChangeValues={values => {
                        if (values?.[0]) {
                          setValue('type_division', values?.[0])
                          setValue(branchKeyField, undefined)
                          setError('type_division', {
                            message: '',
                          })
                        }
                      }}
                      list={Object.keys(divisionLabels).map(key => ({
                        value: key as DivisionType,
                        title: divisionLabels[key as DivisionType],
                      }))}
                    />
                  )
                }}
              />
            </div>
            <div className={styles.rowItem}>
              <Controller
                control={control}
                name={branchKeyField}
                render={({ field: { value } }) => {
                  return (
                    <AppSelect
                      size="extra_small"
                      label="Отдел"
                      withError
                      error={errors[branchKeyField]?.message}
                      text={
                        value
                          ? branchs.find(item => item.id === (value as number))
                              ?.name
                          : ''
                      }
                      onChangeValues={values => {
                        if (values?.[0]) {
                          setValue(branchKeyField, values?.[0])
                          setError(branchKeyField, {
                            message: '',
                          })
                        }
                      }}
                      list={branchs.map(item => ({
                        value: item.id,
                        title: item.name,
                      }))}
                    />
                  )
                }}
              />
            </div>
          </div>

          {isSupervisorsAvailable ? (
            <div className={styles.rowItem}>
              <Controller
                control={control}
                name="supervisor"
                rules={{
                  required: 'Выберите супервайзера',
                  min: {
                    value: KG_PHONE_MAX_LENGTH,
                    message: 'Введите телефон полностью',
                  },
                }}
                render={({ field: { value } }) => {
                  const selectedSupervisor = supersState.data?.results.find(
                    item => item.phone === value,
                  )
                  const text = selectedSupervisor
                    ? getFio(selectedSupervisor)
                    : ''

                  return (
                    <AppSelect
                      required
                      size="extra_small"
                      label="Выберите супервайзера"
                      withError
                      error={errors.supervisor?.message}
                      text={text}
                      onChangeValues={([selectedValue]) => {
                        if (selectedValue) {
                          setValue('supervisor', selectedValue)
                          setError('supervisor', {
                            message: '',
                          })
                        }
                      }}
                      list={
                        supersState.data?.results.map(item => ({
                          value: item.phone,
                          title: getFio(item),
                        })) || []
                      }
                    />
                  )
                }}
              />
            </div>
          ) : null}

          {[
            UserRole.Supervisor,
            UserRole.Consultant,
            UserRole.SPECIALIST_B2B,
          ].includes(watchedRole) && (
            <>
              <h2>Может работать с:</h2>

              <div className={styles.permissions}>
                {permissions.map(permission => {
                  return (
                    <Controller
                      control={control}
                      key={permission.value}
                      name="user_permissions"
                      render={({ field: { value } }) => {
                        return (
                          <AppCkeckbox
                            label={permission.label}
                            checked={value.includes(permission.value)}
                            onChange={() => togglePermission(permission.value)}
                          />
                        )
                      }}
                    />
                  )
                })}
              </div>
              <br />
            </>
          )}
          {canUseKartomat && (
            <div className={styles.row}>
              <div className={styles.rowItem}>
                <Controller
                  control={control}
                  name="kartomat"
                  render={({ field: { value } }) => {
                    const selectedKartomat = kartomatOptions?.filter(
                      sub => value?.includes(sub.value),
                    )
                    let text = ''

                    if (selectedKartomat) {
                      text = selectedKartomat
                        .slice(0, MAX_SHOW_KARTOMAT)
                        .map(item => item.title)
                        .join(', ')

                      if (selectedKartomat.length > MAX_SHOW_KARTOMAT) {
                        text += ` + ${
                          selectedKartomat.length - MAX_SHOW_KARTOMAT
                        }`
                      }
                    }

                    return (
                      <AppSelect
                        multiple
                        size="extra_small"
                        label="Картоматы"
                        withError
                        error={errors.kartomat?.message}
                        text={text}
                        defaultValues={value}
                        onChangeValues={values => {
                          if (Array.isArray(values)) {
                            setValue('kartomat', values)
                            setError('kartomat', {
                              message: '',
                            })
                          }
                        }}
                        list={kartomatOptions}
                      />
                    )
                  }}
                />
              </div>
            </div>
          )}

          {subvisionAvailable && (
            <div className={styles.row}>
              <div className={styles.rowItem}>
                <Controller
                  control={control}
                  name="units_lending"
                  render={({ field: { value } }) => {
                    const selectedUnit = subvisions.find(
                      sub => sub.id === Number(value),
                    )
                    let text = ''

                    if (selectedUnit) {
                      text = `${selectedUnit.code} (${selectedUnit.name})`
                    }

                    return (
                      <InputSelect
                        size="extra_small"
                        label="Подразделение кредитования"
                        text={text}
                        withError
                        error={errors.units_lending?.message}
                        onChangeValue={value => {
                          if (value) {
                            setValue('units_lending', value)
                            setError('units_lending', {
                              message: '',
                            })
                          }
                        }}
                        list={subvisions.map(item => ({
                          value: item.id,
                          title: `${item.code} (${item.name})`,
                        }))}
                      />
                    )
                  }}
                />
              </div>
            </div>
          )}

          {can_m_bonus &&
            (watchedRole === UserRole.Consultant ||
              watchedRole === UserRole.Supervisor) && (
              <div className={styles.row}>
                <div className={styles.rowItem}>
                  <Controller
                    control={control}
                    name="mbonus_department"
                    render={({ field: { value } }) => {
                      const selectedDepartment = mbonusDepatments?.find(
                        sub => sub.value === value,
                      )
                      return (
                        <InputSelect
                          size="extra_small"
                          label="Подразделение Mbonus"
                          text={selectedDepartment?.label || ''}
                          withError
                          error={errors.mbonus_department?.message}
                          onChangeValue={value => {
                            if (value) {
                              setValue('mbonus_department', String(value))
                              setError('mbonus_department', {
                                message: '',
                              })
                            }
                          }}
                          list={mbonusDepatments?.map(item => ({
                            value: item.value,
                            title: item?.label,
                          }))}
                        />
                      )
                    }}
                  />
                </div>
              </div>
            )}

          <div className={styles.row}>
            <div className={styles.rowItem}>
              <TextField
                {...register('first_name', {
                  required: 'Заполните это поле',
                })}
                inputSize="small"
                label="Имя"
                required
                withError
                error={errors.first_name}
              />
            </div>
            <div className={styles.rowItem}>
              <TextField
                {...register('last_name', {
                  required: 'Заполните это поле',
                })}
                inputSize="small"
                label="Фамилия"
                required
                withError
                error={errors.last_name}
              />
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.rowItem}>
              <TextField
                {...register('middle_name')}
                inputSize="small"
                label="Отчество"
                withError
                error={errors.middle_name}
              />
            </div>
            <div className={styles.rowItem}>
              <TextField
                {...register('inn', {
                  required: 'Заполните это поле',
                })}
                inputSize="small"
                label="ИНН"
                required
                withError
                error={errors.inn}
              />
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.rowItem}>
              <Controller
                control={control}
                name="phone"
                rules={{
                  required: 'Введите телефон',
                  min: {
                    value: KG_PHONE_MAX_LENGTH,
                    message: 'Введите телефон полностью',
                  },
                }}
                render={({ field: { value } }) => {
                  return (
                    <TextFieldPhone
                      inputSize="small"
                      onChangePhone={phone => {
                        setValue('phone', phone.formattedPhone)
                      }}
                      value={value}
                      withError
                      error={errors.phone}
                    />
                  )
                }}
              />
            </div>

            <div className={styles.rowItem}>
              <TextField
                {...register('jira_id')}
                inputSize="small"
                label="ID JIRA"
                withError
                error={errors.middle_name}
              />
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.rowItem}>
              <TextField
                {...register('tgm_username', {
                  required: 'Это поле обязательно',
                })}
                inputSize="small"
                label="Telegram"
                required
                withError
                error={errors.tgm_username}
              />
            </div>
            <div className={styles.rowItem}>
              <TextField
                {...register('tgm_user_id', {
                  required: 'Это поле обязательно',
                })}
                onChange={e =>
                  setValue(
                    'tgm_user_id',
                    e.currentTarget.value.replace(/\D/g, ''),
                  )
                }
                inputSize="small"
                label="Telegram ID"
                required
                withError
                error={errors.tgm_user_id}
              />
            </div>
          </div>

          <TextField
            {...register('block_reason')}
            inputSize="small"
            label="Причина блокировки"
            withError
          />

          {!user ? (
            <TextField
              {...register('password', {
                required: 'Введите пароль',
                minLength: {
                  value: 8,
                  message: 'Минимум 8 символов',
                },
              })}
              required
              inputSize="small"
              label="Пароль"
              withError
              error={errors.password}
            />
          ) : null}

          <OnlyViewHoc>
            <AppButton disabled={disabled} type="submit">
              {button}
            </AppButton>
          </OnlyViewHoc>
        </div>
      </form>
    </AppModal>
  )
}
